import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { compareStrings } from '../shared/utils';

const initialState = {
  items: [],
  shouldBeUpdated: true,
  lastUpdate: new Date().toISOString(),
  filterFields: { name: '', type: 'all' },
  order: { by: 'name', direction: 'asc' }
};

// Async thunks
export const loadGroup = createAsyncThunk(
  'group/loadGroup',
  async (_, { dispatch, getState, extra: api }) => {
    dispatch({ type: 'appMain/loadingStart' });
    try {
      const filters = getState().group.filterFields;
      const result = await api.loadGroup(filters);
      dispatch({ type: 'appMain/loadingEnd' });
      return { items: result.items };
    } catch (error) {
      dispatch({ type: 'appMain/loadingEnd' });
      if (error.isAxiosError) {
        dispatch({ type: 'account/logoff' });
        throw error;
      }
      // Convert error to serializable format
      const errorMessage = error.message || 'Um erro ocorreu, tente novamente';
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: errorMessage,
          severity: 'error',
          type: 'error'
        } 
      });
      throw error;
    }
  }
);

export const addGroup = createAsyncThunk(
  'group/addGroup',
  async (group, { dispatch, getState, extra: api }) => {
    dispatch({ type: 'appMain/loadingStart' });
    try {
      await api.addGroup(group);
      dispatch({ type: 'appMain/loadingEnd' });
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: 'Grupo criado com sucesso!',
          severity: 'success',
          type: 'success'
        } 
      });
    } catch (error) {
      dispatch({ type: 'appMain/loadingEnd' });
      if (error.isAxiosError) {
        dispatch({ type: 'account/logoff' });
        throw error;
      }
      // Convert error to serializable format
      const errorMessage = error.message || 'Um erro ocorreu, tente novamente';
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: errorMessage,
          severity: 'error',
          type: 'error'
        } 
      });
      throw error;
    }
  }
);

export const updateGroup = createAsyncThunk(
  'group/updateGroup',
  async ( group, { dispatch, getState, extra: api }) => {
    dispatch({ type: 'appMain/loadingStart' });
    try {
      await api.updateGroup(group);
      dispatch({ type: 'appMain/loadingEnd' });
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: 'Grupo atualizado com sucesso!',
          severity: 'success',
          type: 'success'
        } 
      });
    } catch (error) {
      dispatch({ type: 'appMain/loadingEnd' });
      if (error.isAxiosError) {
        dispatch({ type: 'account/logoff' });
        throw error;
      }
      // Convert error to serializable format
      const errorMessage = error.message || 'Um erro ocorreu, tente novamente';
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: errorMessage,
          severity: 'error',
          type: 'error'
        } 
      });
      throw error;
    }
  }
);

export const deleteGroup = createAsyncThunk(
  'group/deleteGroup',
  async ( id, { dispatch, getState, extra: api }) => {
    dispatch({ type: 'appMain/loadingStart' });
    try {
      await api.deleteGroup(id);
      dispatch({ type: 'appMain/loadingEnd' });
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: 'Grupo excluído com sucesso!',
          severity: 'success',
          type: 'success'
        } 
      });
      return { };
    } catch (error) {
      dispatch({ type: 'appMain/loadingEnd' });
      if (error.isAxiosError) {
        dispatch({ type: 'account/logoff' });
        throw error;
      }
      // Convert error to serializable format
      const errorMessage = error.message || 'Um erro ocorreu, tente novamente';
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: errorMessage,
          severity: 'error',
          type: 'error'
        } 
      });
      throw error;
    }
  }
);

const groupSlice = createSlice({
  name: 'group',
  initialState,
  reducers: {
    updateFilters: (state, action) => {
      state.filterFields = { ...state.filterFields, ...action.payload };
      state.shouldBeUpdated = true;
    },
    updateGroupOrder: (state, action) => {
      const sortedItems = [...state.items].sort((a, b) => {
        switch (action.payload.by) {
          case 'priority':
            return a.priority - b.priority;
          case 'type':
            return compareStrings(a.type, b.type);
          default:
            return compareStrings(a.name, b.name);
        }
      });
      if (action.payload.direction === 'desc') {
        sortedItems.reverse();
      }
      state.items = sortedItems;
      state.order = action.payload;
    },
    clear: () => {
      return initialState;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadGroup.fulfilled, (state, action) => {
        const sortedItems = [...action.payload.items].sort((a, b) => {
          switch (state.order.by) {
            case 'priority':
              return a.priority - b.priority;
            case 'type':
              return compareStrings(a.type, b.type);
            default:
              return compareStrings(a.name, b.name);
          }
        });
        if (state.order.direction === 'desc') {
          sortedItems.reverse();
        }
        state.items = sortedItems;
        state.shouldBeUpdated = false;
        state.lastUpdate = new Date().toISOString();
      })
      .addCase(loadGroup.rejected, (state) => {
        state.shouldBeUpdated = true;
      })
      // Add group cases
      .addCase(addGroup.fulfilled, (state, action) => {
        state.shouldBeUpdated = true;
      })
      // Update group cases
      .addCase(updateGroup.fulfilled, (state, action) => {
        state.shouldBeUpdated = true;
      })
      // Delete group cases
      .addCase(deleteGroup.fulfilled, (state, action) => {
        state.shouldBeUpdated = true;
      });
  }
});

export const { updateFilters, toogleFilters, updateGroupOrder, clear } = groupSlice.actions;

export default groupSlice.reducer;
